import { ref, computed } from 'vue'
import {
  resolvePiAdapter,
  loadPiSDK,
  PI_SDK_SCOPES,
  type PiAdapter,
  type PiAuthResult,
  type PiPaymentCallbacks,
  type PiPaymentRequest,
} from './piAdapter'

export interface PiUser {
  uid: string
  username: string
  avatarUrl?: string
  kycStatus?: string
  walletAddress?: string
}

export type { PiAuthResult, PiPaymentRequest, PiPaymentCallbacks, PiAdapter }

export { isInsidePiBrowser, isPiPlatformContext, detectPiBrowser, getPiBrowserDeeplink, isDesktopBrowser } from './piDetection'
import { isInsidePiBrowser, detectPiBrowser } from './piDetection'

/** Resolve the runtime at sign-in time, including Pi browsers without a UA token. */
export async function preparePiSignIn(): Promise<'sdk' | 'oauth'> {
  if (!isInsidePiBrowser()) {
    try {
      await loadPiSDK()
    } catch {
      // SDK availability alone must not prevent external-browser OAuth.
    }
  }
  return (await detectPiBrowser()) ? 'sdk' : 'oauth'
}

export interface PiOAuthState {
  state: string
  redirectPath: string
}

export interface PiSignInCallbackResult {
  accessToken: string
  redirectPath: string
}

let piInitialized = false

const isReady = ref(false)
const currentUser = ref<PiUser | null>(null)
const isAuthenticated = ref(false)

export function usePi() {
  const hasIdentity = computed(() => !!currentUser.value?.uid && !!currentUser.value?.username)

  const init = async (_appId: string, sandbox = true) => {
    if (piInitialized) return
    const adapter = resolvePiAdapter(isInsidePiBrowser)
    try {
      await adapter.init(_appId, sandbox)
      piInitialized = true
      isReady.value = true
    } catch (err) {
      piInitialized = false
      isReady.value = false
      throw err
    }
  }

  const authenticate = async (
    scopes: string[] = PI_SDK_SCOPES,
    onIncomplete?: (payment: PiIncompletePayment) => void,
  ): Promise<{ accessToken: string; refreshToken?: string; user: PiUser }> => {
    const adapter = resolvePiAdapter(isInsidePiBrowser)
    const result = await adapter.authenticate(scopes, onIncomplete)
    currentUser.value = result.user
    isAuthenticated.value = true
    isReady.value = true
    return result
  }

  const createPayment = async (
    payment: PiPaymentRequest,
    callbacks: PiPaymentCallbacks,
  ): Promise<void> => {
    if (!isReady.value) {
      throw new Error('Pi SDK not initialized')
    }
    resolvePiAdapter(isInsidePiBrowser).createPayment(payment, callbacks)
  }

  return {
    isReady,
    currentUser,
    isAuthenticated,
    hasIdentity,
    init,
    authenticate,
    createPayment,
  }
}

// --- Pi Sign-in (OAuth 2.0 Implicit Flow for external browsers) ---

const PI_OAUTH_STATE_KEY = 'ts_pi_oauth_state'
const PI_SIGNIN_STATE_KEY = 'ts_pi_signin_state'
const PI_SIGNIN_REDIRECT_KEY = 'ts_pi_signin_redirect'
const PI_OAUTH_BASE = 'https://accounts.pinet.com/oauth/authorize'

/**
 * Initiate Pi Sign-in by redirecting to the Pi OAuth authorization server.
 * Uses the OAuth 2.0 Implicit Flow — the access token is returned directly
 * in the URL fragment of the redirect URI.
 */
export function startPiSignIn(redirectPath = '/dashboard/'): void {
  if (isInsidePiBrowser()) {
    throw new Error('Use Pi Browser sign-in here, or open TalentSwap in Safari or Chrome.')
  }
  const clientId = import.meta.env.VITE_PI_OAUTH_CLIENT_ID || ''
  const redirectUri =
    import.meta.env.VITE_PI_SIGNIN_REDIRECT_URI || `${window.location.origin}/auth/pi-callback`

  // Redirecting with an empty client_id makes the authorize page fail silently.
  // Fail loudly here instead so the UI can show what's wrong.
  if (!clientId) {
    throw new Error('Pi Sign-in is not configured: VITE_PI_OAUTH_CLIENT_ID missing from this build')
  }

  // Generate cryptographic random state for CSRF protection
  const stateArray = new Uint8Array(32)
  crypto.getRandomValues(stateArray)
  const state = Array.from(stateArray, (b) => b.toString(16).padStart(2, '0')).join('')

  const oauthState: PiOAuthState = { state, redirectPath }

  sessionStorage.setItem(PI_OAUTH_STATE_KEY, JSON.stringify(oauthState))

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'token',
    // Pi Sign-in only supports "username" / "wallet_address" here.
    // Requesting SDK scopes like "payments" fails with scope_not_allowed
    // and leaves the authorize page hanging on its own loading screen.
    scope: 'username wallet_address',
    state,
  })

  window.location.href = `${PI_OAUTH_BASE}?${params.toString()}`
}

/**
 * Validate the Pi Sign-in callback. Extracts the access token and state from
 * the URL fragment and verifies the state against sessionStorage.
 *
 * OAuth errors may arrive in the fragment or the query string depending on
 * how the authorization server terminates the flow, so both are inspected.
 *
 * Returns the validated callback result, or throws an error.
 */
export function validateSignInCallback(): PiSignInCallbackResult {
  const fragment = new URLSearchParams(window.location.hash.substring(1))
  const query = new URLSearchParams(window.location.search)

  const accessToken = fragment.get('access_token')
  const returnedState = fragment.get('state')
  const error = fragment.get('error') || query.get('error')
  const errorDescription = fragment.get('error_description') || query.get('error_description')

  // Clear URL fragment from browser history immediately so the token isn't visible in address bar.
  // Keep history.state: vue-router stores its back/forward bookkeeping there.
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    window.history.replaceState(window.history.state, '', window.location.pathname)
  }

  // Retrieve and consume saved state
  const rawState = sessionStorage.getItem(PI_OAUTH_STATE_KEY)
  sessionStorage.removeItem(PI_OAUTH_STATE_KEY)
  // Also clean up any legacy storage keys
  sessionStorage.removeItem(PI_SIGNIN_STATE_KEY)
  sessionStorage.removeItem(PI_SIGNIN_REDIRECT_KEY)

  let savedState: PiOAuthState | null = null
  if (rawState) {
    try {
      savedState = JSON.parse(rawState)
    } catch {
      // Invalid format
    }
  }

  if (error) {
    throw new Error(`Pi Sign-in denied: ${errorDescription || error}`)
  }

  if (!accessToken) {
    throw new Error(
      'No sign-in result received from Pi. If this persists, the redirect URI registered in the Pi Developer Portal may not match this page.',
    )
  }

  if (!savedState || savedState.state !== returnedState) {
    throw new Error('Invalid state parameter — the sign-in attempt expired or was tampered with')
  }

  return {
    accessToken,
    redirectPath: savedState.redirectPath || '/dashboard/',
  }
}
